import { useContext, useMemo, useState } from 'react'

import clsx from 'clsx'

import { useMediaActionAutoScheduleMutation } from '@/common/api/modules/mediaAction'
import { AutoScheduleVars } from '@/common/api/params/mediaAction'
import { priorityBadgeType, priorityLabel } from '@/common/app/modules/media/articleCreator'
import { SchedulePlanItem } from '@/common/model/media/mediaAction'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import CheckBox from '@/components/view/atoms/checkbox/CheckBox'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { AutoScheduleModalContext } from '@/components/container/modal/automationReview/AutoScheduleModalProvider'

const FOCUS_MODE_CHOICES = [
  { value: 'focused', label: '集中型', description: '同カテゴリを連続配置（テーマクラスター）' },
  { value: 'balanced', label: 'バランス型', description: '新規/リライトを混ぜて分散（推奨）' },
  { value: 'distributed', label: '分散型', description: '優先度順に均等配置' }
]

const defaultStartDate = () => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

// AI提案でスケジュール作成モーダル（設定 → プレビュー → 適用）
const AutoScheduleModal = () => {
  const { modal, showing, hide } = useContext(AutoScheduleModalContext)

  const [step, setStep] = useState<'settings' | 'preview'>('settings')
  const [horizonDays, setHorizonDays] = useState(90)
  const [maxPerWeek, setMaxPerWeek] = useState(2)
  const [startDate, setStartDate] = useState(defaultStartDate)
  const [weekendOff, setWeekendOff] = useState(true)
  const [focusMode, setFocusMode] = useState('balanced')
  const [useAi, setUseAi] = useState(true)
  const [plan, setPlan] = useState<SchedulePlanItem[] | null>(null)
  const [aiUsed, setAiUsed] = useState(false)

  const buildVars = (apply: boolean): AutoScheduleVars => ({
    site_id: modal.siteId,
    horizon_days: horizonDays,
    max_per_week: maxPerWeek,
    start_date: startDate,
    weekend_off: weekendOff,
    focus_mode: focusMode,
    use_ai: useAi,
    apply
  })

  const { trigger: runSchedule, isMutating: loading } = useMediaActionAutoScheduleMutation({
    onSuccess: (data) => {
      if (data.applied) {
        modal.onApplied?.()
        handleClose()
      } else {
        setPlan(data.plan ?? [])
        setAiUsed(data.ai_used)
        setStep('preview')
      }
    }
  })

  const handleClose = () => {
    hide()
    setStep('settings')
    setPlan(null)
  }

  // プレビューを日付別グループ化
  const grouped = useMemo(() => {
    const groups: Record<string, SchedulePlanItem[]> = {}
    plan?.forEach((p) => {
      const key = p.scheduled_date || '(未割当)'
      if (!groups[key]) groups[key] = []
      groups[key].push(p)
    })
    return groups
  }, [plan])

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader
        text={step === 'settings' ? 'AI提案でスケジュール作成' : 'スケジュール案プレビュー'}
        onClickClose={handleClose}
      />
      <ModalContent>
        {step === 'settings' && (
          <div className='flex flex-col gap-4'>
            <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
              未割当のアクションに対し、SEOノウハウ（テーマクラスター効果・クールダウン・優先度）に基づいた実施スケジュールを生成します。
            </Text>
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              <div className='flex flex-col gap-1.5'>
                <FormLabel fixWidth={null}>期間</FormLabel>
                <div className='flex gap-1.5'>
                  {[30, 60, 90].map((d) => (
                    <Button
                      key={d}
                      color={horizonDays === d ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
                      onClick={() => setHorizonDays(d)}
                    >
                      直近{d}日
                    </Button>
                  ))}
                </div>
              </div>
              <div className='flex flex-col gap-1.5'>
                <FormLabel fixWidth={null}>週あたりの最大件数</FormLabel>
                <TextField
                  type='number'
                  size={TextBoxSize.SMALL}
                  value={maxPerWeek}
                  onChange={(e) =>
                    setMaxPerWeek(Math.max(1, Math.min(5, parseInt(e.target.value) || 2)))
                  }
                />
              </div>
              <div className='flex flex-col gap-1.5'>
                <FormLabel fixWidth={null}>開始日</FormLabel>
                <TextField
                  type='date'
                  size={TextBoxSize.MEDIUM}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className='flex flex-col gap-1.5'>
                <FormLabel fixWidth={null}>土日を含める</FormLabel>
                <CheckBox
                  name='auto-schedule-weekend'
                  label='土日にも配置する'
                  checked={!weekendOff}
                  onChange={(checked) => setWeekendOff(!checked)}
                />
              </div>
            </div>
            <div className='flex flex-col gap-1.5'>
              <FormLabel fixWidth={null}>配置スタイル</FormLabel>
              <div className='flex gap-2'>
                {FOCUS_MODE_CHOICES.map((o) => (
                  <button
                    key={o.value}
                    type='button'
                    className={clsx(
                      'flex-1 cursor-pointer rounded-lg border px-2.5 py-2 text-left',
                      focusMode === o.value
                        ? 'border-focus bg-surface-secondary'
                        : 'border-secondary hover:bg-surface-secondary'
                    )}
                    onClick={() => setFocusMode(o.value)}
                  >
                    <div className='flex flex-col gap-0.5'>
                      <Text
                        size={12}
                        weight='bold'
                        color={focusMode === o.value ? TextColor.LINK : TextColor.SECONDARY}
                      >
                        {o.label}
                      </Text>
                      <Text size={10} color={TextColor.SECONDARY}>
                        {o.description}
                      </Text>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className='flex flex-col gap-1.5'>
              <FormLabel fixWidth={null}>AI 最適化を併用</FormLabel>
              <CheckBox
                name='auto-schedule-use-ai'
                label='ルールベース案を Claude AI で更に最適化（推奨）'
                checked={useAi}
                onChange={(checked) => setUseAi(checked)}
              />
            </div>
          </div>
        )}

        {step === 'preview' && plan && (
          <div className='flex flex-col gap-3'>
            <InsideArea color={BgColor.INFO} border>
              <div className='flex flex-col gap-1'>
                <div className='flex items-center gap-2'>
                  <Text size={12} weight='bold' color={TextColor.LINK}>
                    {plan.length} 件のスケジュール案
                  </Text>
                  {aiUsed && (
                    <Badge size={10} badgeType={BadgeType.SQUARE_AI}>
                      AI最適化済
                    </Badge>
                  )}
                </div>
                <Text size={10} color={TextColor.SECONDARY} lineHeight={150}>
                  未配置の場合は週上限・クールダウンで配置できなかったアクションです。設定を変えるか、期間を延ばして再生成してください。
                </Text>
              </div>
            </InsideArea>
            <div className='flex max-h-[460px] flex-col gap-2 overflow-y-auto'>
              {Object.entries(grouped).map(([date, list]) => (
                <InsideArea key={date} color={BgColor.SECONDARY} border>
                  <div className='flex flex-col gap-1'>
                    <Text
                      size={12}
                      weight='bold'
                      color={date === '(未割当)' ? TextColor.CRITICAL : TextColor.LINK}
                    >
                      {date}{' '}
                      <span className='text-secondary text-[10px] font-normal'>
                        {list.length}件
                      </span>
                    </Text>
                    {list.map((p) => (
                      <div key={p.action_id} className='flex flex-wrap items-baseline gap-1.5'>
                        <Badge size={10} badgeType={priorityBadgeType(p.priority)}>
                          {priorityLabel(p.priority)}
                        </Badge>
                        <Text size={12} weight='bold'>
                          #{p.execution_order}
                        </Text>
                        <Text size={12}>{p.title}</Text>
                        {p.reason && (
                          <Text size={10} color={TextColor.SECONDARY}>
                            — {p.reason}
                          </Text>
                        )}
                      </div>
                    ))}
                  </div>
                </InsideArea>
              ))}
            </div>
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        {step === 'settings' ? (
          <>
            <Button
              color={ButtonColor.PRIMARY}
              size='large'
              loading={loading}
              onClick={() => runSchedule(buildVars(false)).catch(() => undefined)}
            >
              {loading ? '生成中...' : 'プレビューを生成 →'}
            </Button>
            <Button color={ButtonColor.SECONDARY} size='large' onClick={handleClose}>
              キャンセル
            </Button>
          </>
        ) : (
          <>
            <Button
              color={ButtonColor.PRIMARY}
              size='large'
              loading={loading}
              onClick={() => runSchedule(buildVars(true)).catch(() => undefined)}
            >
              {loading ? '適用中...' : 'この計画で適用'}
            </Button>
            <Button color={ButtonColor.SECONDARY} size='large' onClick={() => setStep('settings')}>
              ← 設定に戻って再生成
            </Button>
            <Button color={ButtonColor.SECONDARY} size='large' onClick={handleClose}>
              キャンセル
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default AutoScheduleModal
